import { useState, useEffect, useRef } from 'react';
import { Back, Forward, Google, Star, HalfStart, EmptyStar } from '../../assets/icons';
import { GoogleReviewSkeleton } from '../skeletons/google-review.skeleton';

export const GoogleReviews = ({ reviews, isLoading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const intervalRef = useRef();

  // Touch handling state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef();

  // Minimum swipe distance (în pixeli)
  const minSwipeDistance = 50;

  // Auto rotation every 2 seconds (only on desktop)
  useEffect(() => {
    // Clear any existing interval
    intervalRef.current && clearInterval(intervalRef.current);

    // Only enable auto rotation on desktop (cardsToShow > 1)
    if (!reviews || !reviews.reviews || reviews.reviews.length <= cardsToShow || cardsToShow === 1) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const maxIndex = Math.max(0, reviews.reviews.length - cardsToShow);
        return prevIndex >= maxIndex ? 0 : prevIndex + 1;
      });
    }, 2000);

    return () => clearInterval(intervalRef.current);
  }, [reviews, cardsToShow]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      // Adjust number of cards for different screen sizes
      if (window.innerWidth < 640) { // sm breakpoint
        setCardsToShow(1);
      } else if (window.innerWidth < 1024) { // md breakpoint
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }

      // Make sure current index is valid after resize
      if (reviews && reviews.reviews) {
        const maxIndex = cardsToShow === 1
          ? reviews.reviews.length - 1
          : Math.max(0, reviews.reviews.length - cardsToShow);
        if (currentIndex > maxIndex) {
          setCurrentIndex(maxIndex);
        }
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, [reviews, currentIndex, cardsToShow]);

  const renderStars = (rating) => {
    // Convert rating to a number and ensure it's valid
    const numRating = Number(rating);
    if (isNaN(numRating) || numRating < 0) return null;

    const stars = [];
    const fullStars = Math.floor(numRating);
    const hasHalfStar = (numRating % 1) >= 0.5;
    const totalStars = 5;

    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star
          key={i}
          className="w-4 h-4 fill-yellow-400 text-yellow-400"
        />
      );
    }

    // Add half star if needed
    if (hasHalfStar) {
      stars.push(
        <HalfStart
          key={fullStars}
          className="w-4 h-4 fill-yellow-400 text-yellow-400"
        />
      );
    }

    // Add empty stars to complete 5 stars total
    const filledStars = fullStars + (hasHalfStar ? 1 : 0);
    for (let i = filledStars; i < totalStars; i++) {
      stars.push(
        <EmptyStar
          key={i}
          className="w-4 h-4 fill-gray-200 text-gray-200"
        />
      );
    }

    return stars;
  };

  const toggleReviewExpansion = (index) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const nextSlide = () => {
    if (!reviews || !reviews.reviews) return;
    const maxIndex = cardsToShow === 1
      ? reviews.reviews.length - 1
      : Math.max(0, reviews.reviews.length - cardsToShow);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (!reviews || !reviews.reviews) return;
    const maxIndex = cardsToShow === 1
      ? reviews.reviews.length - 1
      : Math.max(0, reviews.reviews.length - cardsToShow);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (reviews && reviews.reviews && reviews.reviews.length > 1) {
      if (isLeftSwipe) {
        nextSlide();
      }
      if (isRightSwipe) {
        prevSlide();
      }
    }
  };

  // Oprește auto-play când utilizatorul interacționează
  const pauseAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const resumeAutoPlay = () => {
    if (reviews && reviews.reviews && reviews.reviews.length > cardsToShow && cardsToShow > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const maxIndex = Math.max(0, reviews.reviews.length - cardsToShow);
          return prevIndex >= maxIndex ? 0 : prevIndex + 1;
        });
      }, 2000);
    }
  };

  if (isLoading) {
    return (
      <GoogleReviewSkeleton/>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Google className="w-15 h-15 text-blue-500"/>
        <span className="flex items-center font-manrope text-[28px] font-semibold">{reviews.rating}</span>
        <div className={'flex-col'}>
          <span className="flex items-center">{renderStars(reviews.rating)}</span>
          <span className="text-gray-500 text-sm">({reviews.totalRatings} recenzii)</span>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        {/* Left Arrow - ascuns pe mobile */}
        <button
          onClick={prevSlide}
          className="text-gray-400 absolute left-[-15px] top-1/2 -translate-y-1/2 z-10 bg-white/60 hover:bg-white shadow-md hover:shadow-lg rounded-full p-2.5 transition-all duration-200 hover:scale-105 group border border-gray-200 items-center justify-center w-10 h-10 hidden sm:flex"
          aria-label="Previous review"
        >
          <Back className="w-3 h-3" />
        </button>

        {/* Right Arrow - ascuns pe mobile */}
        <button
          onClick={nextSlide}
          className="text-gray-400 absolute right-[-15px] top-1/2 -translate-y-1/2 z-10 bg-white/60 hover:bg-white shadow-md hover:shadow-lg rounded-full p-2.5 transition-all duration-200 hover:scale-105 group border border-gray-200 items-center justify-center w-10 h-10 hidden sm:flex"
          aria-label="Next review"
        >
          <Forward className="w-5 h-5" />
        </button>

        {/* Reviews Container cu touch support */}
        <div
          className="overflow-hidden"
          ref={carouselRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={resumeAutoPlay}
        >
          <div
            className={`flex transition-transform duration-300 ease-in-out ${cardsToShow === 1 ? '' : 'gap-1'}`}
            style={{
              transform: cardsToShow === 1
                ? `translateX(-${currentIndex * 100}%)`
                : `translateX(-${currentIndex * (100/cardsToShow)}%)`
            }}
          >
            {reviews.reviews.map((review, index) => (
              <div
                key={index}
                className={`flex-none w-full ${
                  cardsToShow === 3 ? 'sm:w-1/3' :
                    cardsToShow === 2 ? 'sm:w-1/2' : 'sm:w-full'
                } bg-[#F0F1F5] rounded-lg p-6 shadow-sm ${cardsToShow > 1 ? 'ml-4 first:ml-0' : ''}`}
                style={cardsToShow > 1 ? {
                  flexBasis: `calc(${100/cardsToShow}% - ${(cardsToShow-1)*16/cardsToShow}px)`
                } : {}}
              >
                {/* User Info */}
                <div className="flex items-center mb-4">
                  <img
                    src={review.photo}
                    alt={review.name}
                    className="w-12 h-12 rounded-full mr-3"
                  />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      {review.name}
                    </h4>
                  </div>
                </div>

                {/* Review Content - Fixed Layout */}
                <div className="flex flex-col justify-between min-h-[120px]">
                  {/* Review Text - Expandable */}
                  <div className="mb-3">
                    <div
                      className="cursor-pointer group"
                      onClick={() => toggleReviewExpansion(index)}
                    >
                      <p className={`text-gray-700 text-sm leading-relaxed transition-all duration-300 ${
                        expandedReviews.has(index)
                          ? 'line-clamp-none'
                          : 'line-clamp-4 overflow-hidden'
                      }`}>
                        {review.text}
                      </p>
                      {review.text && review.text.length > 150 && (
                        <button className="text-blue-600 hover:text-blue-700 text-xs font-medium transition-colors ">
                          {expandedReviews.has(index) ? 'Arată mai puțin' : 'Citește mai mult...'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Review Date and Rating - Always visible at bottom */}
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-200">
                    <span className="text-xs text-gray-400">{review.date}</span>
                    <span className="flex items-center flex-shrink-0">{renderStars(review.rating)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Slide indicators pentru mobile */}
        {reviews && reviews.reviews && reviews.reviews.length > 1 && cardsToShow === 1 && (
          <div className="flex justify-center mt-4 space-x-2 sm:hidden">
            {reviews.reviews.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  index === currentIndex
                    ? 'bg-blue-500'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};